/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, no-param-reassign, react/no-unescaped-entities, react/prop-types */
import { api } from "jiffy-api";
import { isElementVisible } from "common/utils/ui/visibility";
import { extractErrorMessage } from "common/utils/extractErrorMessage";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { type canva } from "common/utils/models/canva.types";
import {
  $fileInputAtom,
  $modalAtom,
  designsAtom,
  errorAtom,
  intervalIdAtom,
  loadingAtom,
  settingsAtom,
  uploadingAtom
} from "./canvaModule.atoms";

declare const jQuery: (selector: string | Element | null) => { modal(action: string): void };

type CanvaModuleState = {
  $fileInput: HTMLInputElement | null;
  $modal: HTMLElement | null;
  designs: { continuation: string | undefined; items: canva.designs.Item[] };
  error: string | null;
  intervalId: ReturnType<typeof setInterval> | null;
  loading: boolean;
  settings: canva.settings.Response | Record<string, never>;
  uploading: boolean;
};

type CanvaActionContext = {
  commit: (name: string, payload?: unknown) => void;
  dispatch: (name: string, payload?: unknown) => Promise<unknown>;
  getters: Record<string, unknown>;
  state: CanvaModuleState;
  rootState: Record<string, unknown>;
  rootGetters: Record<string, unknown>;
};

const canvaModule = {
  namespaced: true,

  state() {
    return {
      $fileInput: $fileInputAtom,
      $modal: $modalAtom,
      designs: designsAtom,
      error: errorAtom,
      intervalId: intervalIdAtom,
      loading: loadingAtom,
      settings: settingsAtom,
      uploading: uploadingAtom
    };
  },

  getters: {
    $fileInput: (state: CanvaModuleState) => state.$fileInput,
    $modal: (state: CanvaModuleState) => state.$modal,
    continuation: (state: CanvaModuleState) => state.designs.continuation,
    designs: (state: CanvaModuleState) => state.designs.items,
    error: (state: CanvaModuleState) => state.error,
    intervalId: (state: CanvaModuleState) => state.intervalId,
    loading: (state: CanvaModuleState) => state.loading,
    settings: (state: CanvaModuleState) => state.settings,
    uploading: (state: CanvaModuleState) => state.uploading
  },

  mutations: {
    $setFileInput(state: CanvaModuleState, $fileInput: HTMLInputElement | null) {
      state.$fileInput = $fileInput;
    },
    $setModal(state: CanvaModuleState, $modal: HTMLElement | null) {
      state.$modal = $modal;
    },
    setError(state: CanvaModuleState, error: string) {
      state.error = "Something went wrong, please try again.";

      try {
        const match = error.match(/\{.*\}/);
        const jsonError = JSON.parse(match![0]);
        state.error = `Error: ${jsonError.message}`;
      } catch (_) {
        console.error(error);
      }
    },
    setDesigns(state: CanvaModuleState, designs: canva.designs.Response) {
      state.designs = {
        continuation: designs.continuation,
        items: [...state.designs.items, ...designs.items]
      };
    },
    clearDesigns(state: CanvaModuleState) {
      state.designs = {
        continuation: undefined,
        items: []
      };
    },
    clearError(state: CanvaModuleState) {
      state.error = null;
    },
    setIntervalId(state: CanvaModuleState, id: ReturnType<typeof setInterval>) {
      state.intervalId = id;
    },
    setSettings(
      state: CanvaModuleState,
      settings: canva.settings.Response | Record<string, never>
    ) {
      state.settings = settings;
    },
    toggleLoading(state: CanvaModuleState, force: boolean) {
      state.loading = force;
    },
    toggleUploading(state: CanvaModuleState, force: boolean) {
      state.uploading = force;
    }
  },

  actions: {
    async checkDesignStatus(
      { commit, dispatch, getters }: Pick<CanvaActionContext, "commit" | "dispatch" | "getters">,
      jobId: string
    ) {
      const modalVisible = isElementVisible(getters["$modal"] as HTMLElement | null);
      if (!modalVisible) {
        commit("toggleUploading", false);
        clearInterval(getters["intervalId"] as ReturnType<typeof setInterval>);
        return;
      }

      try {
        const { job } = await api.canva.designStatus({ jobId });

        if (job.status === "success" && modalVisible) {
          clearInterval(getters["intervalId"] as ReturnType<typeof setInterval>);
          await dispatch("setImageFromURL", job.urls[0]);
        } else if (!modalVisible) {
          clearInterval(getters["intervalId"] as ReturnType<typeof setInterval>);
        }
      } catch (e) {
        const message = extractErrorMessage(e);
        commit("setError", message);
      } finally {
        commit("toggleUploading", false);
      }
    },
    async fetchDesigns({ commit }: Pick<CanvaActionContext, "commit">, continuation?: string) {
      commit("toggleLoading", true);

      try {
        const params = continuation ? { continuation } : {};
        const designs = await api.canva.designs(params);
        commit("setDesigns", designs);
      } catch (e) {
        /*
         * 401 is returned when the token is invalid or expired
         * We need to reload the page to get a new token
         */
        if (e instanceof Object && "status" in e && (e as { status: number }).status === 401) {
          window.location.reload();
          return;
        }

        const message = extractErrorMessage(e);
        commit("setError", message);
      } finally {
        commit("toggleLoading", false);
      }
    },
    async fetchSettings({ commit }: Pick<CanvaActionContext, "commit">, namespace: string) {
      try {
        const settings = await api.canva.settings({
          source: namespace,
          redirect_url: window.location.pathname
        });
        commit("setSettings", settings);
      } catch (e) {
        const message = extractErrorMessage(e);
        commit("setError", message);
      }
    },
    async selectDesign(
      { commit, dispatch }: Pick<CanvaActionContext, "commit" | "dispatch">,
      designId: string
    ) {
      commit("toggleUploading", true);

      submitDtfEvent("canva_design_selected", { design_id: designId });

      try {
        const { job } = await api.canva.selectDesign({ designId });
        const intervalId = setInterval(() => {
          dispatch("checkDesignStatus", job.id);
        }, 1000);
        commit("setIntervalId", intervalId);
      } catch (e) {
        const message = extractErrorMessage(e);
        commit("setError", message);
        commit("toggleUploading", false);
      }
    },
    async setImageFromURL(
      { commit, getters }: Pick<CanvaActionContext, "commit" | "getters">,
      url: string
    ) {
      try {
        const response = await fetch(url);
        const blob = await response.blob();

        const changeEvent = new Event("change");
        const urlPath = new URL(url).pathname;
        const fileName = urlPath.substring(urlPath.lastIndexOf("/") + 1);
        const file = new File([blob], fileName, { type: blob.type });
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        const fileInput = getters["$fileInput"] as HTMLInputElement;
        fileInput.files = dataTransfer.files;
        fileInput.dispatchEvent(changeEvent);
        fileInput.setAttribute("data-upload-source", "canva");

        jQuery(getters["$modal"] as HTMLElement | null).modal("hide");
      } catch (e) {
        const message = extractErrorMessage(e);
        commit("setError", message);
      }
    },
    resetModalState({
      commit,
      dispatch,
      getters
    }: Pick<CanvaActionContext, "commit" | "dispatch" | "getters">) {
      commit("toggleUploading", false);
      commit("toggleLoading", false);

      if (getters["error"]) {
        commit("clearError");
        commit("clearDesigns");
        dispatch("fetchDesigns");
      }
    }
  }
};

export default canvaModule;
